import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { DirtyInvalidErrorStateMatcher } from '@examdojo/angular/forms';
import { DateTimeService, Time } from '@examdojo/core/date-time';
import { ComponentSize } from '@examdojo/core/model';
import { distinctUntilChanged, merge, startWith, switchMap, tap } from 'rxjs';
import { DateFilterButtonComponent } from '../date-filter-button/date-filter-button.component';
import { TimePickerButtonComponent } from '../time-picker-button/time-picker-button.component';

@Component({
  selector: 'dojo-datetime-picker-button',
  imports: [DateFilterButtonComponent, TimePickerButtonComponent],
  templateUrl: './datetime-picker-button.component.html',
  styleUrl: './datetime-picker-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatetimePickerButtonComponent {
  constructor(private readonly dateTimeService: DateTimeService) {
    merge(
      this.onControlValueChangeSyncToInternalCtrls(),
      this.onInternalCtrlsChangeSyncToControl(),
      this.onControlStatusChangeSyncDisabledState(),
      this.onControlStatusChangeUpdateChildErrorStateMatcher(),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly control = input.required<FormControl<Date | null>>();
  readonly placeholder = input.required<string>();
  readonly timePlaceholder = input.required<string>();
  readonly min = input<Date | null>(null);
  readonly max = input<Date | null>(null);
  readonly errorStateMatcher = input<ErrorStateMatcher>(new DirtyInvalidErrorStateMatcher());
  readonly size = input<ComponentSize>('s');

  readonly dateCtrl = new FormControl<Date | null>(null);
  readonly timeCtrl = new FormControl<Time | null>(null);

  readonly childErrorStateMatcher = signal<ErrorStateMatcher>(new DirtyInvalidErrorStateMatcher());

  private onControlStatusChangeSyncDisabledState() {
    return toObservable(this.control).pipe(
      switchMap((ctrl) => ctrl.statusChanges.pipe(startWith(ctrl.status))),
      tap((status) => {
        const opts = { emitEvent: false };
        if (status === 'DISABLED') {
          this.dateCtrl.disable(opts);
          this.timeCtrl.disable(opts);
        } else {
          this.dateCtrl.enable(opts);
          this.timeCtrl.enable(opts);
        }
      }),
    );
  }

  private onControlValueChangeSyncToInternalCtrls() {
    return toObservable(this.control).pipe(
      switchMap((ctrl) =>
        ctrl.valueChanges.pipe(
          startWith(ctrl.value),
          distinctUntilChanged((prev, curr) => prev?.getTime() === curr?.getTime()),
        ),
      ),
      tap((value) => {
        this.syncExternalToInternal(value);
      }),
    );
  }

  private onInternalCtrlsChangeSyncToControl() {
    return merge(this.dateCtrl.valueChanges, this.timeCtrl.valueChanges).pipe(
      tap(() => {
        this.syncInternalToExternal();
      }),
    );
  }

  private onControlStatusChangeUpdateChildErrorStateMatcher() {
    return toObservable(this.control).pipe(
      switchMap((ctrl) => merge(ctrl.statusChanges, ctrl.valueChanges).pipe(startWith(null))),
      tap(() => {
        const parentMatcher = this.errorStateMatcher();
        const parentControl = this.control();
        this.childErrorStateMatcher.set({
          isErrorState: () => parentMatcher.isErrorState(parentControl, null),
        } as ErrorStateMatcher);
      }),
    );
  }

  private syncExternalToInternal(value: Date | null): void {
    if (value) {
      this.dateCtrl.setValue(value, { emitEvent: false });
      this.timeCtrl.setValue(this.extractTimeFromDate(value), { emitEvent: false });
    } else {
      this.dateCtrl.setValue(null, { emitEvent: false });
      this.timeCtrl.setValue(null, { emitEvent: false });
    }
  }

  private syncInternalToExternal(): void {
    const date = this.dateCtrl.value;
    const time = this.timeCtrl.value;

    if (date && time) {
      const fullDate = this.applyTimeToDate(date, time);
      this.control().setValue(fullDate, { emitEvent: false });
      this.control().updateValueAndValidity();
      this.control().markAsDirty();
      this.dateCtrl.setValue(fullDate, { emitEvent: false });
    } else if (date && !time) {
      const normalized = this.dateTimeService.createDateTimeInTimezone(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12,
        0,
        'user',
      );
      this.dateCtrl.setValue(normalized, { emitEvent: false });
      if (this.control().value !== null) {
        this.control().setValue(null, { emitEvent: false });
        this.control().updateValueAndValidity();
        this.control().markAsDirty();
      }
    } else if (this.control().value !== null) {
      this.control().setValue(null, { emitEvent: false });
      this.control().updateValueAndValidity();
      this.control().markAsDirty();
    }
  }

  private extractTimeFromDate(date: Date): Time {
    const { hours, minutes: rawMinutes } = this.dateTimeService.extractTimeParts(date);
    // TODO [FE-2860]: Handle it if we want to use 15 minutes intervals
    const minutes = parseInt(rawMinutes) < 30 ? '00' : '30';
    return `${hours}:${minutes}` as Time;
  }

  private applyTimeToDate(date: Date, time: Time): Date {
    const [hours, minutes] = time.split(':').map(Number);
    return this.dateTimeService.createDateTimeInTimezone(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
      'user',
    );
  }
}
